import * as React from 'react';
import { observer, Observer } from 'mobx-react-lite';
import { Table, Space, Tooltip, Button, Popconfirm } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { SortOrder } from 'antd/lib/table/interface';
import { Utils } from '../../../common/services/Utils';
import { TrainingProjectDashboardStore } from '../../stores';
import { TrainingProjectExampleModel } from '../../models';
import useTableScrollHeight from '../../../common/hooks/useTableScrollHeight';
import './TrainingProjectExamplesList.less';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

type Props = {
    store: TrainingProjectDashboardStore;
};

const TrainingProjectExamplesList: React.FC<Props> = ({ store }) => {
    const { scrollHeight } = useTableScrollHeight(280);

    const handleSelectionChange = React.useCallback(
        (selectedRowKeys: React.Key[]) => {
            store.setSelectedExampleIds(selectedRowKeys as string[]);
        },
        [store]
    );

    const columns: ColumnProps<TrainingProjectExampleModel>[] = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            ellipsis: true,
            render: (_: unknown, example: TrainingProjectExampleModel) => (
                <Tooltip title={example.id}>
                    <span>{example.id}</span>
                </Tooltip>
            )
        },
        {
            title: 'Source Type',
            dataIndex: 'sourceType',
            key: 'sourceType',
            render: (_: unknown, example: TrainingProjectExampleModel) => (
                <Observer>{() => <span>{example.sourceType}</span>}</Observer>
            )
        },
        {
            title: 'Source Value',
            dataIndex: 'sourceValue',
            key: 'sourceValue',
            ellipsis: true,
            render: (_: unknown, example: TrainingProjectExampleModel) => (
                <Observer>{() => <span>{example.sourceValueLabel}</span>}</Observer>
            )
        },
        {
            title: 'Document Name',
            dataIndex: 'documentName',
            key: 'documentName',
            ellipsis: true,
            render: (_: unknown, example: TrainingProjectExampleModel) => (
                <Observer>{() => <span>{example.documentName || '-'}</span>}</Observer>
            )
        },
        {
            title: 'Output Type',
            dataIndex: 'outputType',
            key: 'outputType',
            render: (_: unknown, example: TrainingProjectExampleModel) => (
                <Observer>{() => <span>{example.outputType}</span>}</Observer>
            )
        },
        {
            title: 'Output Value',
            dataIndex: 'outputValue',
            key: 'outputValue',
            ellipsis: true,
            render: (_: unknown, example: TrainingProjectExampleModel) => (
                <Observer>{() => <span>{example.outputValue}</span>}</Observer>
            )
        },
        {
            title: 'Last Updated',
            dataIndex: 'updateDate',
            key: 'updateDate',
            sorter: (a: TrainingProjectExampleModel, b: TrainingProjectExampleModel) =>
                Utils.safeDateCompare(a.updateDate || a.createDate, b.updateDate || b.createDate),
            defaultSortOrder: 'descend' as SortOrder,
            render: (_: unknown, example: TrainingProjectExampleModel) => (
                <Observer>
                    {() => (
                        <span>
                            {Utils.formatDateShort(example.updateDate ? example.updateDate : example.createDate)}
                        </span>
                    )}
                </Observer>
            )
        },
        {
            title: 'Valid',
            key: 'jsonValidation',
            render: (_: unknown, example: TrainingProjectExampleModel) => (
                <Observer>
                    {() => {
                        if (example.outputType !== 'Json') {
                            return <span>N/A</span>;
                        }

                        return (
                            <div style={{ marginLeft: 8 }}>
                                {example.isValid && <CheckOutlined style={{ color: 'green' }} />}
                                {example.isValid === false && (
                                    <Tooltip
                                        title={`JSON validation failed: ${example.validationErrors?.join(', ') || 'Unknown error'}`}
                                    >
                                        <CloseOutlined style={{ color: 'red' }} />
                                    </Tooltip>
                                )}
                            </div>
                        );
                    }}
                </Observer>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: unknown, example: TrainingProjectExampleModel) => (
                <div className="row-actions-wrapper" onClick={e => e.stopPropagation()}>
                    <Space size="middle">
                        <Tooltip title="Edit training project example">
                            <Button
                                size="small"
                                type="text"
                                style={{ verticalAlign: 'middle' }}
                                icon={<i className="alpha-icon lg edit-icon" style={{ marginTop: -3 }} />}
                                onClick={() => {
                                    example.setEditable(true);
                                    store.setCreateEditExampleDialogVisible(true);
                                }}
                            />
                        </Tooltip>
                        <Tooltip title="Delete training project example">
                            <Popconfirm
                                title={`Are you sure you want to delete training project example?`}
                                onConfirm={() => example.delete()}
                                okText="Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<i className="alpha-icon lg delete-icon-red" />}
                                    danger
                                />
                            </Popconfirm>
                        </Tooltip>
                    </Space>
                </div>
            )
        }
    ];

    return (
        <Table
            data-id="training-project-examples-table"
            className="alpha-portal-table training-project-examples-list"
            loading={store.isLoadingExamples}
            dataSource={store.isLoadingExamples ? [] : store.examples.slice()}
            columns={columns}
            pagination={{
                pageSize: store.examplesPageSize,
                hideOnSinglePage: false,
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                onShowSizeChange: (_, size) => {
                    store.setExamplesPageSize(size);
                }
            }}
            scroll={{ y: scrollHeight }}
            rowKey={(example: TrainingProjectExampleModel) => example.id}
            onRow={(example: TrainingProjectExampleModel) => {
                return {
                    onClick: () => {
                        example.setEditable(true);
                        store.setCreateEditExampleDialogVisible(true);
                    }
                };
            }}
            rowSelection={{
                selectedRowKeys: store.selectedExampleIds,
                onChange: handleSelectionChange
            }}
        />
    );
};

export default observer(TrainingProjectExamplesList);
